import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  CircularProgress,
  Typography,
  Box,
  TextField,
  Paper,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  createTheme,
  ThemeProvider,
  styled,
  useTheme
} from '@mui/material/styles';
import {
  Store as StoreIcon,
  Search as SearchIcon,
  ArrowUp
} from 'lucide-react';

// Importar tus servicios
import {
  getPublishedProducts,
  publishProduct,
  unpublishProduct,
  recordProductView
} from '../services/AGTUps-product-services/marketplaceService';
import chatService from '../services/AGTUps-product-services/chatService';

// ========== 1) Paleta de colores y theme personalizado ==========
const COLORS = {
  primary: 'rgba(128, 128, 128, 0.5)',
  text: {
    light: '#FFFFFF',
    dark: '#FFFFFF'
  }
};

const createCustomTheme = (mode, actualMode) =>
  createTheme({
    palette: {
      mode,
      actualMode
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none'
            }
          },
          contained: {
            backgroundColor: actualMode === 'wallpaper' ? '#FFFFFF' : COLORS.primary,
            color: actualMode === 'wallpaper' ? '#000000' : '#ffffff',
            '&:hover': {
              backgroundColor: actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.85)' : COLORS.primary
            },
            '&:disabled': {
              backgroundColor:
                mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.12)'
                  : 'rgba(0, 0, 0, 0.12)'
            }
          },
          text: {
            color: actualMode === 'wallpaper' ? '#FFFFFF' : COLORS.text[mode]
          }
        }
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: '16px',
            border: '1px solid',
            borderColor: actualMode === 'wallpaper'
              ? 'rgba(128, 128, 128, 0.3)'
              : (mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)'),
            backgroundColor: actualMode === 'wallpaper' ? 'rgba(128, 128, 128, 0.3)' : undefined,
            backdropFilter: actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
            WebkitBackdropFilter: actualMode === 'wallpaper' ? 'blur(10px)' : 'none'
          }
        }
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: actualMode === 'wallpaper' ? 'rgba(128, 128, 128, 0.25)' : undefined,
              backdropFilter: actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
              WebkitBackdropFilter: actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
              '& fieldset': {
                borderColor: actualMode === 'wallpaper' ? 'rgba(128, 128, 128, 0.3)' : undefined
              },
              '& input, & textarea': {
                color: actualMode === 'wallpaper' ? '#FFFFFF' : undefined
              }
            },
            '& .MuiInputLabel-root': {
              color: actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.7)' : undefined
            }
          }
        }
      }
    }
  });

// ========== 2) Estilos y helpers ==========
const ScrollContainer = styled(Box)(() => ({
  maxHeight: '70vh', // para que no rebase la ventana
  overflowY: 'auto',
  width: '100%',
  padding: '16px',
  '&::-webkit-scrollbar': { width: '8px' },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0, 0, 0, 0.2)',
    borderRadius: '4px'
  }
}));

const ProductCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(3),
  textAlign: 'center',
  minHeight: 320,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  boxShadow: 'none'
}));

const ImageContainer = styled(Box)(() => ({
  height: 140,
  maxWidth: '100%',
  overflow: 'hidden',
  marginBottom: '8px',
  borderRadius: '12px',
  position: 'relative',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  }
}));

const OverlayLabel = styled(Box)(() => ({
  position: 'absolute',
  top: 4,
  left: 4,
  backgroundColor: 'rgba(255, 255, 255, 0.7)',
  color: '#000',
  padding: '2px 6px',
  borderRadius: 4,
  fontSize: '0.75rem',
  fontWeight: 'bold'
}));

// Extraer usuario de localStorage
const firebaseAuthKey =
  'firebase:authUser:AIzaSyCl_QY3Y0RYRNuMc541mwhBlMW2VbLiqhQ:[DEFAULT]';
const storedUser = localStorage.getItem(firebaseAuthKey);
const firebaseUser = storedUser ? JSON.parse(storedUser) : null;
const localUserId = firebaseUser?.uid || '';

/* =========================================================================
   3) Componente interno: StoreShortcutContent
      - Contiene toda la lógica (tabs, producto, modal, etc.)
   =========================================================================*/
function StoreShortcutContent({ open, onClose, products }) {

  // Estados
  const [allPublished, setAllPublished] = useState([]);
  const [activeTab, setActiveTab] = useState(1); // 0 = "My Published", 1 = "For You"
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [modalProductOpen, setModalProductOpen] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const [chatMessage, setChatMessage] = useState('Hola, ¿todavía está disponible?');
  const [isSending, setIsSending] = useState(false);

  const [negotiationOpen, setNegotiationOpen] = useState(false);

  const showMessage = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Fetch all published
  const fetchAllPublishedProducts = async () => {
    setLoading(true);
    try {
      const response = await getPublishedProducts(false);
      setAllPublished(response.data || []);
    } catch (error) {
      console.error('Error fetching published:', error);
      showMessage('Error al cargar productos publicados', 'error');
      setAllPublished([]);
    } finally {
      setLoading(false);
    }
  };

  // Al abrir el Shortcut, cargar los productos
  useEffect(() => {
    if (open) {
      fetchAllPublishedProducts();
    }
    // eslint-disable-next-line
  }, [open]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Abrir modal de producto
  const handleOpenProductModal = async (item) => {
    try {
      if (item.userId !== localUserId) {
        await recordProductView(item.productId);
      }
    } catch (err) {
      console.error('Error al registrar vista:', err);
    }
    setSelectedProduct(item);
    setModalProductOpen(true);
  };

  const handleCloseProductModal = () => {
    setSelectedProduct(null);
    setModalProductOpen(false);
    setChatMessage('Hola, ¿todavía está disponible?');
  };

  // Publicar
  const handlePublishProduct = async (product) => {
    try {
      const resp = await publishProduct(product.id, product);
      if (resp.success) {
        showMessage('Producto publicado correctamente');
        fetchAllPublishedProducts();
      } else {
        showMessage('Error al publicar', 'error');
      }
    } catch (error) {
      console.error('Publish error:', error);
      showMessage('Error al publicar', 'error');
    }
  };

  // Despublicar
  const handleUnpublishProduct = async (product) => {
    try {
      const resp = await unpublishProduct(product.productId);
      if (resp.success) {
        showMessage('Producto despublicado correctamente');
        fetchAllPublishedProducts();
      } else {
        showMessage('Error al despublicar', 'error');
      }
    } catch (error) {
      console.error('Unpublish error:', error);
      showMessage('Error al despublicar', 'error');
    }
  };

  // Enviar mensaje
  const handleSendMessage = async () => {
    if (!chatMessage.trim() || !selectedProduct) return;

    if (selectedProduct.userId === localUserId) {
      showMessage('No puedes enviarte mensajes a ti mismo', 'error');
      return;
    }
    setIsSending(true);
    try {
      const chatId = chatService.formatChatId(localUserId, selectedProduct.userId);
      const msgData = {
        chatId,
        senderId: localUserId,
        receiverId: selectedProduct.userId,
        message: chatMessage.trim(),
        productName: selectedProduct.name,
        productImage: selectedProduct.imageUrl || '',
        otherCompanyName: selectedProduct.companyName
      };
      const resp = await chatService.sendMessage(msgData);
      if (resp.success) {
        showMessage('Mensaje enviado');
        setChatMessage('Hola, ¿todavía está disponible?');
      }
    } catch (error) {
      console.error('Send msg error:', error);
      showMessage('Error al enviar mensaje', 'error');
    } finally {
      setIsSending(false);
    }
  };

  // Negociación con IA
  const handleOpenNegotiation = () => {
    if (selectedProduct?.userId === localUserId) {
      showMessage('No puedes negociar tu propio producto', 'error');
      return;
    }
    setNegotiationOpen(true);
  };

  const handleCloseNegotiation = () => {
    setNegotiationOpen(false);
  };

  const handleNegotiateWithAI = async () => {
    if (!selectedProduct) return;
    setIsSending(true);
    try {
      const chatId = chatService.formatChatId(localUserId, selectedProduct.userId);
      const resp = await chatService.negotiateWithAI(
        chatId,
        selectedProduct.productId,
        selectedProduct.userId
      );
      if (resp.success) {
        showMessage('Negociación con IA iniciada. Revisa tus chats.');
      } else {
        showMessage('Error al iniciar negociación con IA', 'error');
      }
    } catch (error) {
      console.error('Negotiate error:', error);
      showMessage('Error al negociar con IA', 'error');
    } finally {
      setIsSending(false);
      handleCloseNegotiation();
    }
  };

  // Listas
  const publishedList = allPublished || [];
  const myPublished = publishedList.filter((p) => p.userId === localUserId);
  const myInventoryNotPublished = (products || []).filter(
    (p) =>
      !publishedList.some(
        (pub) => pub.productId === p.id || pub.productId === p.productId
      )
  );

  let displayedProducts = [];
  if (activeTab === 0) {
    // My Published
    displayedProducts = myPublished.length > 0 ? myPublished : myInventoryNotPublished;
  } else {
    // For You
    displayedProducts = publishedList;
  }

  // Búsqueda local
  const filteredDisplayedProducts = displayedProducts.filter((p) =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Render
  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="lg"
        aria-labelledby="store-dialog-title"
      >
        <DialogTitle id="store-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StoreIcon size={24} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Store (View All)
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          {/* BUSCADOR y TABS */}
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              placeholder="Buscar productos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon size={18} />
                  </InputAdornment>
                )
              }}
              sx={{ mb: 2 }}
            />

            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              textColor="primary"
              indicatorColor="primary"
            >
              <Tab label="My Published" />
              <Tab label="For You" />
            </Tabs>
          </Box>

          {/* Scrollable container de productos */}
          <ScrollContainer>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredDisplayedProducts.length === 0 ? (
              <Typography variant="body2">No hay productos para mostrar.</Typography>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                  gap: 2
                }}
              >
                {filteredDisplayedProducts.map((item) => (
                  <ProductCard key={item.productId || item.id}>
                    <ImageContainer>
                      {item.imageUrl && <img src={item.imageUrl} alt={item.name} />}
                      {item.userId === localUserId && (
                        <OverlayLabel>Mi producto</OverlayLabel>
                      )}
                    </ImageContainer>

                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {item.name}
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#666', mb: 1 }}>
                      ${item.price}
                    </Typography>

                    {activeTab === 0 ? (
                      // My Published
                      <>
                        {myPublished.some((p) => p.productId === item.productId) ? (
                          <Button
                            variant="contained"
                            color="error"
                            onClick={() => handleUnpublishProduct(item)}
                          >
                            Unpublish
                          </Button>
                        ) : (
                          <Button variant="contained" onClick={() => handlePublishProduct(item)}>
                            Publish
                          </Button>
                        )}
                      </>
                    ) : (
                      // For You
                      <Button
                        variant="outlined"
                        onClick={() => handleOpenProductModal(item)}
                        sx={{ textTransform: 'none' }}
                      >
                        Ver detalles
                      </Button>
                    )}
                  </ProductCard>
                ))}
              </Box>
            )}
          </ScrollContainer>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>

        {/* SNACKBAR */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Dialog>

      {/* Modal secundario de detalle del producto */}
      {selectedProduct && (
        <Dialog
          open={modalProductOpen}
          onClose={handleCloseProductModal}
          fullWidth
          maxWidth="sm"
          aria-labelledby="product-detail-dialog"
        >
          <DialogTitle id="product-detail-dialog">
            {selectedProduct.name}
          </DialogTitle>
          <DialogContent dividers>
            {selectedProduct.imageUrl && (
              <Box
                component="img"
                src={selectedProduct.imageUrl}
                alt={selectedProduct.name}
                sx={{
                  width: '100%',
                  maxHeight: 300,
                  objectFit: 'cover',
                  borderRadius: 2,
                  mb: 2
                }}
              />
            )}

            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              ${selectedProduct.price}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mb: 2 }}>
              {selectedProduct.description}
            </Typography>

            {/* Chat input (solo si no es mi producto) */}
            {selectedProduct.userId !== localUserId && (
              <>
                <TextField
                  fullWidth
                  multiline
                  maxRows={3}
                  label="Mensaje"
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleSendMessage}>
                          {isSending ? (
                            <CircularProgress size={20} />
                          ) : (
                            <ArrowUp size={20} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
                <Box sx={{ mt: 2, textAlign: 'right' }}>
                  <Button
                    variant="contained"
                    onClick={handleOpenNegotiation}
                    disabled={isSending}
                  >
                    Negotiate with AI
                  </Button>
                </Box>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseProductModal}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal de negociación con IA */}
      <Dialog
        open={negotiationOpen}
        onClose={handleCloseNegotiation}
        fullWidth
        maxWidth="sm"
        aria-labelledby="negotiation-ia-dialog"
      >
        <DialogTitle id="negotiation-ia-dialog">Negociar con IA</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            La IA iniciará una conversación automáticamente para negociar un
            precio óptimo. Por favor, revisa tu chat para ver el progreso.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNegotiation}>Cancelar</Button>
          <Button variant="contained" onClick={handleNegotiateWithAI} disabled={isSending}>
            {isSending ? <CircularProgress size={20} /> : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/* =========================================================================
   4) Componente principal (default export):
      - Llama a useTheme() para obtener modo del theme padre
      - Crea el customTheme
      - Monta <StoreShortcutContent> dentro del ThemeProvider
   =========================================================================*/

export default function StoreShortcut({ open, onClose, products }) {
  // Tomamos el modo del theme "padre"
  const parentTheme = useTheme();
  // Creamos nuestro customTheme
  const customTheme = createCustomTheme(parentTheme.palette.mode, parentTheme.palette.actualMode);

  return (
    <ThemeProvider theme={customTheme}>
      <StoreShortcutContent open={open} onClose={onClose} products={products} />
    </ThemeProvider>
  );
}
